<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { onClickOutside } from '@vueuse/core';
import {
  UiText, UiIcon, UiBackButton, UiDialog,
  UiDetailTabs, UiDetailTabsItem, UiLockedBadge, UiAvatar,
} from '@shopexperts/shared';
import { useAdminExpertDetail } from '@/app/composables/expert-manager/useAdminExpertDetail';
import { useExpertStatusActions } from '@/app/composables/expert-manager/useExpertStatusActions';
import { useLoginAsExpert } from '@/app/composables/expert-manager/useLoginAsExpert';
import StatusConfirmDialog from '@/app/components/expert-manager/StatusConfirmDialog.vue';
import ExpertTypeSwitchDialog from '@/app/components/expert-manager/ExpertTypeSwitchDialog.vue';
import ExpertSlugDialog from '@/app/components/expert-manager/ExpertSlugDialog.vue';
import ExpertInviteEmailDialog from '@/app/components/expert-manager/ExpertInviteEmailDialog.vue';
import ImportReviewsFlow from '@/app/components/expert-manager/import-reviews/ImportReviewsFlow.vue';
import ExpertReviewsPanel from '@/app/components/expert-manager/reviews/ExpertReviewsPanel.vue';
import ExpertBadgesTab from '@/app/components/expert-manager/ExpertBadgesTab.vue';
import ExpertSubscriptionTab from '@/app/components/expert-manager/ExpertSubscriptionTab.vue';
import ExpertConfidenceSignalsTab from '@/app/components/expert-manager/ExpertConfidenceSignalsTab.vue';
import ExpertAnalyticsTab from '@/app/components/expert-manager/ExpertAnalyticsTab.vue';
import ExpertNotesTab from '@/app/components/expert-manager/ExpertNotesTab.vue';
import ExpertTagsEditor from '@/app/components/expert-manager/ExpertTagsEditor.vue';
import ExpertOverviewTab from '@/app/components/expert-manager/details/ExpertOverviewTab.vue';
import AdminExpertWalletLevelHeader from '@/app/components/expert-manager/wallet/AdminExpertWalletLevelHeader.vue';
import AdminWalletTransactionsTab from '@/app/components/expert-manager/wallet/AdminWalletTransactionsTab.vue';
import AdminPayoutRequestsTab from '@/app/components/expert-manager/payouts/AdminPayoutRequestsTab.vue';
import AdminExpertLevelHistoryTab from '@/app/components/expert-manager/wallet/AdminExpertLevelHistoryTab.vue';
import ExpertAiSummariesTab from '@/app/components/expert-manager/ai-summaries/ExpertAiSummariesTab.vue';
import { getExpertTypeAppearance } from '@/app/components/expert-manager/expertTypeAppearance';
import { expertProfileUrl } from '@/app/components/expert-manager/expertSlug';
import LoadingCard from '@/components/common/LoadingCard.vue';
import type { ExpertProfileStatus } from '@shopexperts/legacy-api';

const route = useRoute();
const router = useRouter();

const expertId = computed(() => route.params.expertId as string);
const { expert, isLoading, error } = useAdminExpertDetail(expertId);

const {
  activateApplication,
  publishExpert,
  unpublishExpert,
  setDraft,
  setApplication,
  deleteExpert,
  resendActivation,
} = useExpertStatusActions();

const { loginAs, isLoading: isLoginAsLoading } = useLoginAsExpert();

const activeTab = ref('overview');

const transactionsTabRef = ref<InstanceType<typeof AdminWalletTransactionsTab> | null>(null);
const overviewTabRef = ref<InstanceType<typeof ExpertOverviewTab> | null>(null);
const expertOrganization = computed(() => expert.value?.organization ?? null);
const expertOrganizationId = computed(() => expertOrganization.value?.id ?? expert.value?.organization_id ?? null);

function handleCreateTransaction() {
  activeTab.value = 'transactions';
  nextTick(() => transactionsTabRef.value?.openCreate());
}

async function handleEditDetails() {
  activeTab.value = 'overview';
  // Tab panels mount with v-if, so coming from another tab the overview ref appears a tick later.
  for (let attempt = 0; attempt < 3 && !overviewTabRef.value; attempt += 1) {
    await nextTick();
  }
  overviewTabRef.value?.openSection('listing');
}

const statusDialogOpen = ref(false);
const statusDialogLoading = ref(false);
const statusDialogAction = ref<'activate' | 'publish' | 'unpublish' | 'setDraft' | 'setApplication' | 'deleteExpert' | null>(null);
const loginAsDialogOpen = ref(false);
const switchTypeDialogOpen = ref(false);
const slugDialogOpen = ref(false);
const importReviewsOpen = ref(false);
const inviteDialogOpen = ref(false);

const menuOpen = ref(false);
const menuRef = ref<HTMLElement | null>(null);
onClickOutside(menuRef, () => { menuOpen.value = false; });

const statusClasses = computed(() => {
  if (!expert.value) return 'text-gray-500 bg-gray-50';
  const map: Record<ExpertProfileStatus, string> = {
    published: 'text-emerald-700 bg-emerald-50',
    application: 'text-amber-700 bg-amber-50',
    draft: 'text-gray-500 bg-gray-50',
    unpublished: 'text-rose-600 bg-rose-50',
    banned: 'text-gray-700 bg-gray-100',
  };
  return map[expert.value.profile_status] ?? 'text-gray-500 bg-gray-50';
});

const stripColor = computed(() => {
  if (!expert.value) return 'bg-gray-300';
  const map: Record<ExpertProfileStatus, string> = {
    published: 'bg-emerald-400',
    application: 'bg-amber-400',
    draft: 'bg-gray-300',
    unpublished: 'bg-rose-400',
    banned: 'bg-gray-700',
  };
  return map[expert.value.profile_status] ?? 'bg-gray-300';
});

const tierDisplay = computed(() => {
  if (!expert.value?.partner_tier || expert.value.partner_tier === 'none') return null;
  return expert.value.partner_tier.charAt(0).toUpperCase() + expert.value.partner_tier.slice(1);
});

const locationLabel = computed(() => [expert.value?.city?.name, expert.value?.country].filter(Boolean).join(', '));

const typeDisplay = computed(() => {
  if (!expert.value) return '';
  return getExpertTypeAppearance(expert.value.listing_type).label;
});

const avatarRingClass = computed(() => {
  return getExpertTypeAppearance(expert.value?.listing_type).avatarRingClass;
});

const typeTextClass = computed(() => {
  return getExpertTypeAppearance(expert.value?.listing_type).typeTextClass;
});

const listingUrl = computed(() => {
  if (!expert.value?.slug) return '';
  return expertProfileUrl(expert.value.listing_type, expert.value.slug);
});

/** Ownership plus the most recent claim request's state, folded into one chip. */
const claimDisplay = computed(() => {
  if (!expert.value || expert.value.is_claimed === undefined) return null;
  if (expert.value.is_claimed) {
    return { label: 'Claimed', dotClass: 'bg-emerald-500', textClass: 'text-emerald-700' };
  }
  const byClaimStatus: Record<string, { label: string; dotClass: string; textClass: string }> = {
    pending: { label: 'Unclaimed · claim pending verification', dotClass: 'bg-amber-400', textClass: 'text-amber-700' },
    verified: { label: 'Unclaimed · claim awaiting review', dotClass: 'bg-amber-400', textClass: 'text-amber-700' },
    rejected: { label: 'Unclaimed · last claim rejected', dotClass: 'bg-rose-400', textClass: 'text-rose-600' },
  };
  return (expert.value.latest_claim_status && byClaimStatus[expert.value.latest_claim_status])
    || { label: 'Unclaimed', dotClass: 'bg-orange-400', textClass: 'text-orange-600' };
});

interface MenuItem {
  key: string;
  label: string;
  icon: string;
  variant?: 'default' | 'danger';
}

const menuItems = computed<MenuItem[]>(() => {
  if (!expert.value) return [];
  const items: MenuItem[] = [];
  const s = expert.value.profile_status;

  if (s === 'application') {
    items.push({ key: 'activate', label: 'Activate Application', icon: 'check', variant: 'default' });
  }
  if (s === 'draft' || s === 'unpublished') {
    items.push({ key: 'publish', label: 'Publish', icon: 'check-circle', variant: 'default' });
  }
  if (s === 'published') {
    items.push({ key: 'unpublish', label: 'Unpublish', icon: 'close', variant: 'danger' });
  }
  if (s === 'published' || s === 'unpublished') {
    items.push({ key: 'setDraft', label: 'Set to Draft', icon: 'pencil', variant: 'default' });
  }
  if (s === 'draft') {
    items.push({ key: 'setApplication', label: 'Set to Application', icon: 'arrow-send', variant: 'default' });
    items.push({ key: 'resendActivation', label: 'Resend Activation Email', icon: 'arrow-send', variant: 'default' });
  }
  if (expert.value.is_claimed === false) {
    items.push({ key: 'sendInvite', label: 'Send Invite Email', icon: 'arrow-send', variant: 'default' });
  }

  items.push({ key: 'editDetails', label: 'Edit Details', icon: 'pencil', variant: 'default' });
  items.push({ key: 'switchType', label: 'Switch Type', icon: 'arrow-send', variant: 'default' });
  items.push({ key: 'changeSlug', label: 'Change Profile URL', icon: 'globe-01', variant: 'default' });
  items.push({ key: 'importReviews', label: 'Import Reviews', icon: 'upload', variant: 'default' });
  items.push({ key: 'loginAs', label: 'Login As Expert', icon: 'link-external', variant: 'default' });

  items.push({ key: 'deleteExpert', label: 'Delete Expert Account', icon: 'close', variant: 'danger' });

  return items;
});

function handleMenuAction(action: string) {
  menuOpen.value = false;
  if (action === 'loginAs') {
    loginAsDialogOpen.value = true;
  } else if (action === 'resendActivation') {
    handleResendActivation();
  } else if (action === 'editDetails') {
    handleEditDetails();
  } else if (action === 'switchType') {
    switchTypeDialogOpen.value = true;
  } else if (action === 'changeSlug') {
    slugDialogOpen.value = true;
  } else if (action === 'importReviews') {
    importReviewsOpen.value = true;
  } else if (action === 'sendInvite') {
    inviteDialogOpen.value = true;
  } else {
    statusDialogAction.value = action as any;
    statusDialogOpen.value = true;
  }
}

async function confirmStatusAction() {
  if (!expert.value || !statusDialogAction.value || statusDialogLoading.value) return;
  statusDialogLoading.value = true;
  try {
    const id = expert.value.id;
    switch (statusDialogAction.value) {
      case 'activate': await activateApplication.mutateAsync(id); break;
      case 'publish': await publishExpert.mutateAsync(id); break;
      case 'unpublish': await unpublishExpert.mutateAsync(id); break;
      case 'setDraft': await setDraft.mutateAsync(id); break;
      case 'setApplication': await setApplication.mutateAsync(id); break;
      case 'deleteExpert':
        await deleteExpert.mutateAsync(id);
        router.push({ name: 'admin-expert-manager' });
        break;
    }
    statusDialogOpen.value = false;
  } catch {
    // The mutation displays the server message; keep the confirmation open for retry.
  } finally {
    statusDialogLoading.value = false;
  }
}

async function handleResendActivation() {
  if (!expert.value) return;
  await resendActivation.mutateAsync(expert.value.id);
}

async function confirmLoginAs() {
  if (!expert.value) return;
  await loginAs(expert.value.user_id);
  loginAsDialogOpen.value = false;
}

function formatDate(dateStr: string | null | undefined) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

// Use real history navigation so the previous Expert Manager URL (with
// search/filter query params + scroll position) is restored. Fall back to
// pushing the manager route if the user landed here directly (no history).
function goBack() {
  if (window.history.state?.back) {
    router.back();
  } else {
    router.push({ name: 'admin-expert-manager' });
  }
}
</script>

<template>
  <main class="flex-1 p-8 overflow-y-auto bg-secondary font-light">
    <UiBackButton class="mb-6" @click.prevent="goBack" />

    <LoadingCard v-if="isLoading" />

    <div v-else-if="error" class="text-center py-12">
      <UiText as="p" variant="p" class="text-red-500">Failed to load expert details</UiText>
    </div>

    <template v-else-if="expert">
      <!-- Expert Header — mirrors card design language -->
      <div class="bg-white rounded-lg border border-gray-100 mb-6">
        <div class="flex">
          <!-- Accent strip -->
          <div class="w-1.5 shrink-0 rounded-l-lg" :class="stripColor" />

          <div class="flex-1 min-w-0 p-6 pl-5">
            <div class="flex gap-5">
              <!-- Avatar -->
              <UiAvatar
                :name="expert.display_name"
                :url="expert.avatar ?? undefined"
                size="sm"
                :responsive="false"
                color-coded
                class="self-start ring-2 ring-offset-2 ring-offset-white"
                :class="avatarRingClass"
              />

              <!-- Content -->
              <div class="flex-1 min-w-0">
                <!-- Row 1: Name + PRO | Status + context menu -->
                <div class="flex items-center justify-between gap-4">
                  <div class="flex items-center gap-3 min-w-0">
                    <UiText
                      as="h1"
                      variant="h3"
                      :text="expert.display_name"
                      class="font-semibold text-gray-900"
                    />
                    <UiLockedBadge v-if="expert.is_pro" />
                  </div>

                  <div class="flex items-center gap-2.5 shrink-0">
                    <UiText
                      as="span"
                      variant="caption"
                      class="tracking-wide px-2.5 py-1 rounded"
                      :class="statusClasses"
                    >
                      {{ expert.profile_status }}
                    </UiText>

                    <!-- Context menu — same pattern as card -->
                    <div ref="menuRef" class="relative">
                      <button
                        @click="menuOpen = !menuOpen"
                        aria-label="Expert actions"
                        class="p-1.5 rounded-md transition-colors text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                        :class="{ 'bg-gray-100 text-gray-700!': menuOpen }"
                      >
                        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <circle cx="10" cy="4" r="1.5" />
                          <circle cx="10" cy="10" r="1.5" />
                          <circle cx="10" cy="16" r="1.5" />
                        </svg>
                      </button>

                      <Transition
                        enter-active-class="transition duration-100 ease-out"
                        enter-from-class="opacity-0 scale-95"
                        enter-to-class="opacity-100 scale-100"
                        leave-active-class="transition duration-75 ease-in"
                        leave-from-class="opacity-100 scale-100"
                        leave-to-class="opacity-0 scale-95"
                      >
                        <div
                          v-if="menuOpen"
                          class="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg border border-gray-200 shadow-lg z-50 py-1 origin-top-right"
                        >
                          <template v-for="(item, idx) in menuItems" :key="item.key">
                            <div
                              v-if="item.key === 'loginAs' && idx > 0"
                              class="my-1 border-t border-gray-100"
                            />
                            <button
                              class="w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors"
                              :class="item.variant === 'danger'
                                ? 'text-red-600 hover:bg-red-50'
                                : 'text-gray-700 hover:bg-gray-50'
                              "
                              @click="handleMenuAction(item.key)"
                            >
                              <UiIcon :name="(item.icon as any)" class="w-4 h-4 shrink-0 opacity-60" />
                              <UiText as="span" variant="p-small">{{ item.label }}</UiText>
                            </button>
                          </template>
                        </div>
                      </Transition>
                    </div>
                  </div>
                </div>

                <!-- Row 2: Meta line -->
                <div class="flex items-center gap-1.5 mt-1.5">
                  <UiText as="span" variant="caption-normal" :class="typeTextClass">
                    {{ typeDisplay }}
                  </UiText>
                  <template v-if="tierDisplay">
                    <span class="text-gray-300">·</span>
                    <UiText as="span" variant="caption-normal" class="text-se-highlight font-medium">
                      {{ tierDisplay }}
                    </UiText>
                  </template>
                  <template v-if="expert.email">
                    <span class="text-gray-300">·</span>
                    <UiText as="span" variant="caption-normal" class="text-gray-500">
                      {{ expert.email }}
                    </UiText>
                  </template>
                  <template v-if="locationLabel">
                    <span class="text-gray-300">·</span>
                    <UiText as="span" variant="caption-normal" class="text-gray-500">
                      {{ locationLabel }}
                    </UiText>
                  </template>
                  <template v-if="expert.timezone">
                    <span class="text-gray-300">·</span>
                    <UiText as="span" variant="caption-normal" class="text-gray-400">
                      {{ expert.timezone }}
                    </UiText>
                  </template>
                </div>

                <!-- Row 3: Public listing URL — the address this profile lives at -->
                <div class="flex items-center gap-2 mt-2">
                  <UiIcon name="link-external" class="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <a
                    v-if="expert.slug && listingUrl"
                    :href="listingUrl"
                    target="_blank"
                    class="font-mono text-xs text-se-highlight hover:underline truncate"
                  >{{ listingUrl.replace(/^https?:\/\//, '') }}</a>
                  <UiText v-else as="span" variant="caption-normal" class="text-gray-400">
                    No public URL yet
                  </UiText>
                  <button
                    type="button"
                    class="shrink-0 p-1 -m-1 rounded text-gray-400 transition-colors hover:text-gray-700 hover:bg-gray-50"
                    :aria-label="expert.slug ? 'Change profile URL' : 'Set profile URL'"
                    @click="slugDialogOpen = true"
                  >
                    <UiIcon name="pencil" class="w-3.5 h-3.5" />
                  </button>
                </div>

                <!-- Row 4: Stats -->
                <div class="flex items-center gap-5 mt-3 flex-wrap">
                  <div v-if="expert.reviews_count > 0" class="flex items-center gap-1.5">
                    <UiIcon name="star" class="w-4 h-4 text-amber-500 fill-amber-500" />
                    <UiText as="span" variant="p-small" class="font-semibold text-gray-800">
                      {{ expert.average_rating?.toFixed(1) }}
                    </UiText>
                    <UiText as="span" variant="p-small" class="text-gray-500">
                      ({{ expert.reviews_count }} reviews)
                    </UiText>
                  </div>
                  <div v-if="expert.starting_price" class="flex items-center gap-1.5">
                    <UiIcon name="dollar-circle" class="w-4 h-4 text-gray-400" />
                    <UiText as="span" variant="p-small" class="text-gray-600">
                      From <span class="font-medium text-gray-800">${{ expert.starting_price.toLocaleString() }}</span>
                    </UiText>
                  </div>
                  <div v-if="expert.languages?.length" class="flex items-center gap-1.5">
                    <UiIcon name="map-pin" class="w-4 h-4 text-gray-400" />
                    <UiText as="span" variant="p-small" class="text-gray-500">
                      {{ expert.languages.join(', ') }}
                    </UiText>
                  </div>
                  <div class="flex items-center gap-1.5">
                    <UiIcon name="calendar-color" class="w-4 h-4 text-gray-400" />
                    <UiText as="span" variant="p-small" class="text-gray-500">
                      Joined {{ formatDate(expert.created_at) }}
                    </UiText>
                  </div>
                  <div v-if="claimDisplay" class="flex items-center gap-1.5">
                    <span class="w-1.5 h-1.5 rounded-full shrink-0" :class="claimDisplay.dotClass" />
                    <UiText as="span" variant="p-small" :class="claimDisplay.textClass">
                      {{ claimDisplay.label }}
                    </UiText>
                  </div>
                </div>

                <!-- Row 5: Internal tags — admin-only segmentation, drives the manager filters -->
                <div class="mt-3 flex flex-wrap items-center gap-2">
                  <UiText as="span" variant="caption" class="tracking-wide text-gray-400">TAGS</UiText>
                  <ExpertTagsEditor :expert-id="expert.id" :tags="expert.tags" />
                </div>

                <!-- Row 6: Services — micro tag clusters with vertical accent -->
                <div v-if="expert.offered_services.length > 0" class="mt-3.5 flex flex-wrap gap-x-5 gap-y-2">
                  <div
                    v-for="service in expert.offered_services"
                    :key="service.service_category.id"
                    class="flex gap-1.5 min-w-0"
                  >
                    <div class="w-px rounded-full bg-gray-200 shrink-0 self-stretch" />
                    <div class="min-w-0">
                      <span class="text-[10px] font-semibold uppercase tracking-wider text-gray-500 leading-none">{{ service.service_category.name }}</span>
                      <p v-if="service.sub_categories.length > 0" class="text-[11px] text-gray-400 leading-snug mt-px">
                        {{ service.sub_categories.map(s => s.name).join(', ') }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Wallet & level summary -->
      <div class="mb-6">
        <AdminExpertWalletLevelHeader
          :expert-id="expert.id"
          :organization="expertOrganization"
          :organization-id="expertOrganizationId"
          :expert-name="expert.display_name"
          @create-transaction="handleCreateTransaction"
        />
      </div>

      <!-- Tabs -->
      <div class="bg-white rounded-lg border border-gray-100">
        <UiDetailTabs v-model="activeTab" defaultValue="overview">
          <UiDetailTabsItem value="overview" label="Overview">
            <ExpertOverviewTab ref="overviewTabRef" :expert="expert" />
          </UiDetailTabsItem>

          <UiDetailTabsItem value="notes" label="Notes" :count="expert.notes_count">
            <ExpertNotesTab :expert-id="expert.id" />
          </UiDetailTabsItem>

          <UiDetailTabsItem value="analytics" label="Analytics">
            <ExpertAnalyticsTab :expert-id="expert.id" />
          </UiDetailTabsItem>

          <UiDetailTabsItem value="badges" label="App Skills">
            <ExpertBadgesTab :expert="expert" />
          </UiDetailTabsItem>

          <UiDetailTabsItem value="trust-signals" label="Trust Signals">
            <ExpertConfidenceSignalsTab :expert="expert" />
          </UiDetailTabsItem>

          <UiDetailTabsItem value="subscription" label="Subscription & Invoices">
            <ExpertSubscriptionTab :expert="expert" />
          </UiDetailTabsItem>

          <UiDetailTabsItem value="transactions" label="Transactions">
            <AdminWalletTransactionsTab
              ref="transactionsTabRef"
              :organization="expertOrganization"
              :organization-id="expertOrganizationId"
            />
          </UiDetailTabsItem>

          <UiDetailTabsItem value="payouts" label="Payouts">
            <AdminPayoutRequestsTab :organization-id="expertOrganizationId" />
          </UiDetailTabsItem>

          <UiDetailTabsItem value="level-history" label="Level History">
            <AdminExpertLevelHistoryTab :expert-id="expert.id" />
          </UiDetailTabsItem>

          <UiDetailTabsItem value="ai-summaries" label="AI Summaries">
            <ExpertAiSummariesTab :expert-id="expert.id" />
          </UiDetailTabsItem>
          <UiDetailTabsItem value="reviews" label="Reviews">
            <ExpertReviewsPanel :expert-id="expert.id" />
          </UiDetailTabsItem>

          <UiDetailTabsItem value="projects" label="Projects & Leads" :disabled="true">
            <div class="text-center py-12">
              <UiIcon name="category" class="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <UiText as="p" variant="p" class="text-gray-400">Projects & leads management coming soon</UiText>
            </div>
          </UiDetailTabsItem>

          <UiDetailTabsItem value="stories" label="Case Studies" :disabled="true">
            <div class="text-center py-12">
              <UiIcon name="user-circle" class="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <UiText as="p" variant="p" class="text-gray-400">Case studies management coming soon</UiText>
            </div>
          </UiDetailTabsItem>

          <UiDetailTabsItem value="packages" label="Packaged Services" :disabled="true">
            <div class="text-center py-12">
              <UiIcon name="package-placeholder" class="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <UiText as="p" variant="p" class="text-gray-400">Packaged services management coming soon</UiText>
            </div>
          </UiDetailTabsItem>
        </UiDetailTabs>
      </div>
    </template>

    <!-- Status Confirm Dialog -->
    <StatusConfirmDialog
      v-if="expert"
      v-model="statusDialogOpen"
      v-model:loading="statusDialogLoading"
      :action="statusDialogAction"
      :expert-name="expert.display_name"
      :current-status="expert.profile_status"
      @confirm="confirmStatusAction"
    />

    <!-- Switch Type Dialog -->
    <ExpertTypeSwitchDialog
      v-if="expert"
      v-model="switchTypeDialogOpen"
      :expert-id="expert.id"
      :expert-name="expert.display_name"
      :current-listing-type="expert.listing_type"
    />

    <!-- Change Profile URL Dialog -->
    <ExpertSlugDialog
      v-if="expert"
      v-model="slugDialogOpen"
      :expert-id="expert.id"
      :expert-name="expert.display_name"
      :listing-type="expert.listing_type"
      :current-slug="expert.slug"
    />

    <!-- Invite Email Dialog -->
    <ExpertInviteEmailDialog
      v-if="expert"
      v-model="inviteDialogOpen"
      :expert-id="expert.id"
    />

    <!-- Import Reviews Flow -->
    <ImportReviewsFlow
      v-if="expert"
      v-model:open="importReviewsOpen"
      :expert-id="expert.id"
      :expert-name="expert.display_name"
    />

    <!-- Login As Dialog -->
    <UiDialog
      v-model="loginAsDialogOpen"
      title="Login as Expert"
      primaryActionLabel="Login As Expert"
      secondaryActionLabel="Cancel"
      size="sm"
      :loading="isLoginAsLoading"
      @primaryAction="confirmLoginAs"
      @secondaryAction="loginAsDialogOpen = false"
      @close="loginAsDialogOpen = false"
    >
      <div v-if="expert" class="flex items-center gap-4 mb-4">
        <UiAvatar
          :name="expert.display_name"
          :url="expert.avatar ?? undefined"
          size="xs"
          :responsive="false"
          color-coded
        />
        <div>
          <UiText as="p" variant="p" class="font-medium text-gray-900">{{ expert.display_name }}</UiText>
          <UiText as="p" variant="p-small" class="text-gray-500">{{ expert.email }}</UiText>
        </div>
      </div>
      <UiText as="p" variant="p-small" class="text-gray-600">
        You will be redirected to their dashboard where you can view and manage their account.
      </UiText>
    </UiDialog>
  </main>
</template>
